import React from 'react';
import { isFeatureEnabled, siteConfig } from './utils';

interface FeatureGuardProps {
    feature: keyof typeof siteConfig.features;
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

/**
 * Компонент для условного рендеринга контента на основе включенных функций
 */
export function FeatureGuard({ feature, children, fallback = null }: FeatureGuardProps) {
    if (isFeatureEnabled(feature)) {
        return <>{children}</>;
    }
    
    return <>{fallback}</>;
}

interface MultiFeatureGuardProps {
    features: Array<keyof typeof siteConfig.features>;
    operator?: 'AND' | 'OR';
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

/**
 * Компонент для условного рендеринга контента на основе нескольких функций
 */
export function MultiFeatureGuard({ 
    features, 
    operator = 'AND', 
    children, 
    fallback = null 
}: MultiFeatureGuardProps) {
    const shouldRender = operator === 'AND' 
        ? features.every(feature => isFeatureEnabled(feature))
        : features.some(feature => isFeatureEnabled(feature));
    
    if (shouldRender) {
        return <>{children}</>;
    }
    
    return <>{fallback}</>;
}

// Хук для проверки функций в компонентах
export function useFeature(feature: keyof typeof siteConfig.features) {
    const enabled = isFeatureEnabled(feature);
    const config = siteConfig.features[feature]?.config;
    
    return {
        enabled,
        config
    };
}

// Хук для проверки нескольких функций
export function useFeatures(features: Array<keyof typeof siteConfig.features>) {
    const results = features.reduce((acc, feature) => {
        acc[feature] = {
            enabled: isFeatureEnabled(feature),
            config: siteConfig.features[feature]?.config
        };
        return acc;
    }, {} as Record<string, { enabled: boolean; config?: any }>);
    
    return results;
}

export default FeatureGuard; 